import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'

import {
  getKWSyncWorklistCSV,
  type KWSyncGateMetrics,
  type KWSyncGateNamespace
} from '../../../services/etl/etlService'

const NAMESPACE_LABELS: Record<KWSyncGateNamespace, string> = {
  G: 'G — Government',
  E: 'E — County/Municipal'
}

const formatCount = (value: number): string => value.toLocaleString('en-US')

interface KWSyncGatePanelProps {
  jobId: number
  completed: boolean
  metrics: KWSyncGateMetrics
}

export const KWSyncGatePanel: React.FC<KWSyncGatePanelProps> = ({ jobId, completed, metrics }) => {
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  const handleDownload = async (namespace: KWSyncGateNamespace): Promise<void> => {
    setDownloading(true)
    setError(null)
    try {
      const blob = await getKWSyncWorklistCSV(jobId, namespace)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `kw_sync_${jobId}_${namespace}_orphans.csv`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch {
      setError('Failed to download the orphan worklist CSV')
    } finally {
      setDownloading(false)
    }
  }

  const namespaces = Object.keys(metrics.kw_sync_gate) as KWSyncGateNamespace[]

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Orphan Scan</Typography>
      {error !== null && <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null) }}>{error}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Namespace</strong></TableCell>
            <TableCell align="right"><strong>Total</strong></TableCell>
            <TableCell align="right"><strong>Matched</strong></TableCell>
            <TableCell align="right"><strong>Orphans</strong></TableCell>
            <TableCell><strong>Worklist</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {namespaces.map((namespace) => {
            const counts = metrics.kw_sync_gate[namespace]
            return (
              <TableRow key={namespace}>
                <TableCell>{NAMESPACE_LABELS[namespace]}</TableCell>
                <TableCell align="right">{formatCount(counts.total)}</TableCell>
                <TableCell align="right">{formatCount(counts.matched)}</TableCell>
                <TableCell align="right">{formatCount(counts.orphans)}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={!completed || downloading || counts.orphans === 0}
                    onClick={() => { void handleDownload(namespace) }}
                  >
                    Orphans CSV
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}
